/**
 * Custom property stamped on every sheet the add-in creates. Cleanup only
 * ever deletes sheets carrying it, so a user's own "Ventas" or "Costos"
 * sheet in the same workbook is never touched.
 */
export const OWNED_SHEET_KEY = "ExcelImparable";

/**
 * Deletes a sheet the add-in created earlier. If a sheet with that name
 * exists but belongs to the user, stop instead of destroying their data.
 */
export async function safeDeleteSheet(
  context: Excel.RequestContext,
  sheetName: string
): Promise<void> {
  const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
  await context.sync();
  if (sheet.isNullObject) return;

  const tag = sheet.customProperties.getItemOrNullObject(OWNED_SHEET_KEY);
  await context.sync();
  if (tag.isNullObject) {
    throw new Error(
      `Este libro ya tiene una hoja llamada "${sheetName}". Para no modificar tu información, abre un libro en blanco para hacer esta práctica.`
    );
  }

  sheet.delete();
  await context.sync();
}

/**
 * Deletes all sheets except the default "Hoja1" / "Sheet1".
 */
export async function deleteAllExtraSheets(
  context: Excel.RequestContext
): Promise<void> {
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();

  const keepNames = ["hoja1", "sheet1", "resultados"];
  for (const sheet of sheets.items) {
    if (!keepNames.includes(sheet.name.toLowerCase())) {
      sheet.delete();
    }
  }
  await context.sync();
}

/**
 * Creates a sheet (deletes it first if it already exists).
 */
export async function createFreshSheet(
  context: Excel.RequestContext,
  name: string
): Promise<Excel.Worksheet> {
  await safeDeleteSheet(context, name);
  const sheet = context.workbook.worksheets.add(name);
  return sheet;
}
